import axios from 'axios';

// WordPress REST API Base URL aus dem globalen rbsAjax Objekt
const API_BASE_URL = window.rbsAjax?.rest_url || '/wp-json/restaurant-booking/v1/';

// Axios-Instanz mit Standard-Konfiguration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request-Interceptor für Nonce-Authentication
api.interceptors.request.use((config) => {
  // Nur für bestimmte Endpunkte Nonce hinzufügen
  const requiresAuth = config.url.includes('/bookings') && config.method.toLowerCase() !== 'get';
  
  if (requiresAuth && window.rbsAjax?.nonce) {
    config.headers['X-WP-Nonce'] = window.rbsAjax.nonce;
  }
  
  if (window.rbsAjax?.debug) {
    console.log('API Request:', {
      url: config.url,
      method: config.method,
      requiresAuth,
      hasNonce: !!window.rbsAjax?.nonce,
      headers: config.headers
    });
  }
  
  return config;
});

// Response-Interceptor für Error-Handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    console.log('Request config:', error.config);
    console.log('Response data:', error.response?.data);
    console.log('Response status:', error.response?.status);
    console.log('Response headers:', error.response?.headers);
    
    // rbsAjax Objekt prüfen
    if (!window.rbsAjax) {
      console.error('rbsAjax Objekt nicht verfügbar!');
      throw new Error('WordPress Integration fehlt - rbsAjax Objekt nicht gefunden');
    }
    
    // REST API URL prüfen
    if (!window.rbsAjax.rest_url) {
      console.error('REST API URL nicht verfügbar!');
      throw new Error('REST API URL nicht konfiguriert');
    }
    
    // Nonce prüfen
    if (!window.rbsAjax.nonce) {
      console.warn('Nonce nicht verfügbar - Authentifizierung könnte fehlschlagen');
    }
    
    if (error.response?.status === 401) {
      throw new Error('Authentifizierung fehlgeschlagen - Nonce ungültig oder abgelaufen');
    } else if (error.response?.status === 403) {
      throw new Error('Zugriff verweigert - Berechtigung fehlt oder REST API deaktiviert');
    } else if (error.response?.status === 404) {
      throw new Error('API-Endpunkt nicht gefunden - Plugin möglicherweise nicht aktiviert');
    } else if (error.response?.status >= 500) {
      throw new Error('Server-Fehler. Bitte versuchen Sie es später erneut.');
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Anfrage-Timeout - Server antwortet nicht');
    } else if (error.message === 'Network Error') {
      throw new Error('Netzwerk-Fehler - Verbindung zum Server fehlgeschlagen');
    } else {
      throw new Error('Ein unerwarteter Fehler ist aufgetreten: ' + (error.message || 'Unbekannter Fehler'));
    }
  }
);

// Test-Funktion für REST API Konnektivität
export const testApiConnection = async () => {
  console.log('=== REST API Connection Test ===');
  
  // rbsAjax Objekt prüfen
  console.log('rbsAjax Objekt:', window.rbsAjax);
  
  if (!window.rbsAjax) {
    throw new Error('rbsAjax Objekt nicht verfügbar - WordPress Integration fehlt');
  }
  
  console.log('Base URL:', API_BASE_URL);
  console.log('REST URL:', window.rbsAjax.rest_url);
  console.log('Nonce:', window.rbsAjax.nonce ? 'Vorhanden' : 'Fehlt');
  
  // Direkter WordPress REST API Test
  try {
    console.log('Teste WordPress REST API Core...');
    const coreResponse = await fetch('/wp-json/');
    const coreData = await coreResponse.json();
    console.log('WordPress REST API Core funktioniert:', coreData);
  } catch (error) {
    console.error('WordPress REST API Core Fehler:', error);
    throw new Error('WordPress REST API ist nicht verfügbar');
  }
  
  // Plugin REST API Test
  try {
    console.log('Teste Plugin REST API...');
    const pluginResponse = await fetch(window.rbsAjax.rest_url + 'settings');
    console.log('Plugin API Response Status:', pluginResponse.status);
    console.log('Plugin API Response Headers:', [...pluginResponse.headers]);
    
    if (!pluginResponse.ok) {
      const errorText = await pluginResponse.text();
      console.error('Plugin API Error Body:', errorText);
      throw new Error(`Plugin API Fehler: ${pluginResponse.status} - ${errorText}`);
    }
    
    const pluginData = await pluginResponse.json();
    console.log('Plugin REST API funktioniert:', pluginData);
    return true;
  } catch (error) {
    console.error('Plugin REST API Fehler:', error);
    throw error;
  }
};

// Restaurant-Einstellungen abrufen
export const getRestaurantSettings = async () => {
  try {
    const response = await api.get('/settings');
    return response.data;
  } catch (error) {
    console.log('REST API fehlgeschlagen, versuche AJAX-Fallback...');
    
    // Fallback: WordPress AJAX API
    if (window.rbsAjax?.ajaxurl) {
      try {
        const ajaxResponse = await fetch(window.rbsAjax.ajaxurl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 'rbs_get_settings'
          }),
          credentials: 'same-origin'
        });
        
        if (!ajaxResponse.ok) {
          throw new Error(`AJAX-Anfrage fehlgeschlagen: ${ajaxResponse.status}`);
        }
        
        const ajaxData = await ajaxResponse.json();
        
        if (ajaxData.success) {
          console.log('AJAX-Fallback erfolgreich - Daten erhalten:', ajaxData.data);
          return ajaxData.data;
        } else {
          throw new Error(`AJAX-Fehler: ${ajaxData.data || 'Unbekannter Fehler'}`);
        }
      } catch (ajaxError) {
        console.error('AJAX-Fallback ebenfalls fehlgeschlagen:', ajaxError);
      }
    }
    
    // Letzter Fallback: Direkte fetch-Anfrage
    if (window.rbsAjax?.rest_url) {
      try {
        const fallbackResponse = await fetch(window.rbsAjax.rest_url + 'settings', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin'
        });
        
        if (!fallbackResponse.ok) {
          throw new Error(`Direkter Fallback fehlgeschlagen: ${fallbackResponse.status}`);
        }
        
        const data = await fallbackResponse.json();
        console.log('Direkter Fallback erfolgreich - Daten erhalten:', data);
        return data;
      } catch (fallbackError) {
        console.error('Direkter Fallback ebenfalls fehlgeschlagen:', fallbackError);
      }
    }
    
    // Alle Fallbacks fehlgeschlagen
    throw error;
  }
};

// Verfügbarkeit für ein bestimmtes Datum und Zeit prüfen
export const checkAvailability = async (date, time, guests) => {
  try {
    const response = await api.get('/availability', {
      params: { date, time, guests }
    });
    return response.data;
  } catch (error) {
    // AJAX-Fallback für Verfügbarkeit
    if (window.rbsAjax?.ajaxurl) {
      try {
        const ajaxResponse = await fetch(window.rbsAjax.ajaxurl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 'rbs_get_availability',
            date: date,
            time: time,
            guests: guests
          }),
          credentials: 'same-origin'
        });
        
        const ajaxData = await ajaxResponse.json();
        if (ajaxData.success) {
          return ajaxData.data;
        }
      } catch (ajaxError) {
        console.error('AJAX-Fallback für Verfügbarkeit fehlgeschlagen:', ajaxError);
      }
    }
    throw error;
  }
};

// Cache für Zeitslots
const timeSlotsCache = new Map();

// Alle verfügbaren Zeitslots für ein Datum abrufen
export const getAvailableTimeSlots = async (date, guests) => {
  try {
    // Prüfen, ob Ergebnisse im Cache sind
    const cacheKey = `${date}_${guests}`;
    if (timeSlotsCache.has(cacheKey)) {
      console.log('🔄 Returning cached time slots for', date);
      return timeSlotsCache.get(cacheKey);
    }

    console.time('getAvailableTimeSlots');
    
    // Settings laden um Öffnungszeiten zu bekommen
    const settings = await getRestaurantSettings();
    
    // Prüfen ob das gewählte Datum heute ist
    const selectedDate = new Date(date);
    const today = new Date();
    const isToday = selectedDate.toDateString() === today.toDateString();
    const currentTime = isToday ? today.getTime() : null;
    
    // Zeitslots für Abendservice generieren
    const eveningSlots = generateTimeSlots(
      settings.opening_time, 
      settings.last_booking_time || settings.closing_time, 
      settings.time_slot_duration
    );
    
    // Zeitslots für Mittagstisch generieren (falls aktiviert UND verfügbar für dieses Datum)
    let lunchSlots = [];
    if (settings.enable_lunch_service && isLunchServiceAvailable(date, settings)) {
      lunchSlots = generateTimeSlots(
        settings.lunch_opening_time,
        settings.lunch_last_booking,
        settings.time_slot_duration
      );
    }
    
    // Alle Zeitslots kombinieren
    let allTimeSlots = [...lunchSlots, ...eveningSlots];
    
    // Vergangene Zeitslots für heute herausfiltern
    if (isToday && currentTime) {
      allTimeSlots = allTimeSlots.filter(timeSlot => {
        const slotTime = parseTimeForDate(timeSlot, selectedDate);
        const timeDiffMinutes = (slotTime - currentTime) / (60 * 1000);
        const isAvailable = timeDiffMinutes >= 2; // 2-Minuten-Puffer - minimal aber realistisch
        return isAvailable;
      });
    }
    
    // Sortieren
    allTimeSlots.sort();
    
    // Optimierung: Batched API-Anfragen statt einzelner Anfragen
    // Wir senden eine einzelne API-Anfrage für alle Zeitslots
    try {
      // Simulieren der Verfügbarkeit für alle Zeitslots (temporär)
      // In einer echten Implementierung würde hier eine einzelne API-Anfrage alle Zeitslots auf einmal prüfen
      const availability = allTimeSlots.map(time => ({
        time,
        available: true, // Standardmäßig verfügbar
        service: getServiceType(time, settings)
      }));
      
      // Ergebnisse im Cache speichern
      timeSlotsCache.set(cacheKey, availability);
      
      console.timeEnd('getAvailableTimeSlots');
      return availability;
    } catch (error) {
      console.error('Fehler bei der Batch-Verfügbarkeitsprüfung:', error);
      
      // Fallback zur ursprünglichen Methode, falls die Batch-Anfrage fehlschlägt
      const availabilityPromises = allTimeSlots.map(async (time) => {
        try {
          const availability = await checkAvailability(date, time, guests);
          return {
            time,
            available: availability.available,
            service: getServiceType(time, settings)
          };
        } catch (error) {
          console.warn(`Verfügbarkeitsprüfung für ${time} fehlgeschlagen:`, error);
          return {
            time,
            available: false,
            service: getServiceType(time, settings)
          };
        }
      });
      
      const availability = await Promise.all(availabilityPromises);
      
      // Auch Fallback-Ergebnisse cachen
      timeSlotsCache.set(cacheKey, availability);
      
      console.timeEnd('getAvailableTimeSlots');
      return availability;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Zeitslots:', error);
    throw error;
  }
};

// Hilfsfunktion zur Bestimmung des Service-Typs
const getServiceType = (time, settings) => {
  const timeValue = parseInt(time.replace(':', ''));
  const lunchEnd = settings.lunch_last_booking ? parseInt(settings.lunch_last_booking.replace(':', '')) : 0;
  
  if (settings.enable_lunch_service && timeValue <= lunchEnd) {
    return 'lunch';
  }
  return 'dinner';
};

// Prüft ob Mittagstisch-Service für ein bestimmtes Datum verfügbar ist
const isLunchServiceAvailable = (date, settings) => {
  if (!settings.enable_lunch_service) {
    return false;
  }
  
  const selectedDate = new Date(date);
  const dayOfWeek = selectedDate.getDay(); // 0 = Sonntag, 1 = Montag, etc.
  const dateString = formatDate(selectedDate);
  
  // Prüfen ob es ein "Nur-Mittag-Tag" ist (kein Abendservice)
  if (settings.lunch_only_dates && settings.lunch_only_dates.includes(dateString)) {
    return true;
  }
  
  // Prüfen ob es ein spezieller Mittagstag ist (Feiertage, etc.)
  if (settings.lunch_special_dates && settings.lunch_special_dates.includes(dateString)) {
    return true;
  }
  
  // Prüfen ob der Wochentag für Mittagstisch aktiviert ist
  if (settings.lunch_weekdays && settings.lunch_weekdays.includes(dayOfWeek)) {
    return true;
  }
  
  return false;
};

// Zeit-String zu vollständigem Timestamp für ein bestimmtes Datum konvertieren
const parseTimeForDate = (timeString, date) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const targetDate = new Date(date);
  targetDate.setHours(hours, minutes, 0, 0);
  return targetDate.getTime();
};

// Neue Buchung erstellen
export const createBooking = async (bookingData) => {
  try {
    const response = await api.post('/bookings', bookingData);
    return response.data;
  } catch (error) {
    // AJAX-Fallback für Buchung
    if (window.rbsAjax?.ajaxurl && window.rbsAjax?.nonce) {
      try {
        const ajaxResponse = await fetch(window.rbsAjax.ajaxurl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 'rbs_create_booking',
            nonce: window.rbsAjax.nonce,
            date: bookingData.date,
            time: bookingData.time,
            guests: bookingData.guests,
            name: bookingData.name,
            email: bookingData.email,
            phone: bookingData.phone || ''
          }),
          credentials: 'same-origin'
        });
        
        const ajaxData = await ajaxResponse.json();
        if (ajaxData.success) {
          return ajaxData.data;
        } else {
          throw new Error(ajaxData.data || 'Buchung fehlgeschlagen');
        }
      } catch (ajaxError) {
        console.error('AJAX-Fallback für Buchung fehlgeschlagen:', ajaxError);
      }
    }
    throw error;
  }
};

// Buchungen für Admin abrufen
export const getBookings = async (startDate, endDate) => {
  try {
    const response = await api.get('/bookings', {
      params: { start_date: startDate, end_date: endDate }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Hilfsfunktion zur Generierung von Zeitslots
const generateTimeSlots = (openingTime, closingTime, duration) => {
  const slots = [];
  const start = parseTime(openingTime);
  const end = parseTime(closingTime);
  
  let current = start;
  while (current <= end) { // <= statt < um die Endzeit einzuschließen
    slots.push(formatTime(current));
    current += duration * 60000; // duration in Millisekunden
  }
  
  console.log(`🕐 Generated slots from ${openingTime} to ${closingTime}:`, slots);
  
  return slots;
};

// Zeit-String zu Timestamp konvertieren (für heute)
const parseTime = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const today = new Date();
  today.setHours(hours, minutes, 0, 0);
  return today.getTime();
};

// Timestamp zu Zeit-String formatieren
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('de-DE', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
};

// Datum formatieren für API - Zeitzonen-sicher
export const formatDate = (date) => {
  if (typeof date === 'string') {
    // Wenn bereits String, prüfen ob es im YYYY-MM-DD Format ist
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
  }
  
  // Für Date-Objekte: Zeitzone-sichere Formatierung
  const d = new Date(date);
  
  // Lokales Datum verwenden (ohne UTC-Verschiebung)
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// Datum für Anzeige formatieren
export const formatDateForDisplay = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

export const isDateClosed = (date, settings) => {
  // Mehrere Formate für robuste Datums-Verarbeitung
  let dateString;
  
  if (typeof date === 'string') {
    // String direkt verwenden wenn im korrekten Format
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateString = date;
    } else {
      // String zu Date konvertieren und dann formatieren
      const dateObj = new Date(date);
      dateString = formatDate(dateObj);
    }
  } else {
    // Date-Objekt formatieren
    dateString = formatDate(date);
  }
  
  const dateObj = new Date(date);
  const dayOfWeek = dateObj.getDay(); // 0 = Sonntag, 1 = Montag, etc.
  
  // HÖCHSTE PRIORITÄT: Individuelle Schließtage
  // Wenn in closed_dates, dann ist es definitiv geschlossen (kann nicht überschrieben werden)
  if (settings.closed_dates && settings.closed_dates.includes(dateString)) {
    console.log('🚫 Date is closed (in closed_dates):', dateString);
    return true;
  }
  
  // MITTLERE PRIORITÄT: Ausnahme-Öffnungstage
  // Wenn in exception_open_dates, dann ist es geöffnet (überschreibt closed_weekdays)
  if (settings.exception_open_dates && settings.exception_open_dates.includes(dateString)) {
    console.log('✅ Date is open (exception_open_dates):', dateString);
    return false; // Explizit geöffnet
  }
  
  // NIEDRIGSTE PRIORITÄT: Geschlossene Wochentage
  if (settings.closed_weekdays && settings.closed_weekdays.includes(dayOfWeek)) {
    console.log('🚫 Date is closed (weekday):', dateString, 'dayOfWeek:', dayOfWeek);
    return true;
  }
  
  // Erweiteres Debug-Logging
  console.log('🔍 isDateClosed Debug:', {
    originalInput: date,
    dateString: dateString,
    dateObj: dateObj,
    dayOfWeek: dayOfWeek,
    closedDates: settings.closed_dates,
    exceptionOpenDates: settings.exception_open_dates,
    closedWeekdays: settings.closed_weekdays,
    isClosed: false,
    type: typeof date
  });
  
  return false; // Standardmäßig geöffnet
};

export const isDateInFuture = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  
  return checkDate >= today;
};

export const isDateWithinAdvanceLimit = (date, maxAdvanceDays) => {
  const today = new Date();
  const maxDate = new Date();
  maxDate.setDate(today.getDate() + maxAdvanceDays);
  
  const checkDate = new Date(date);
  return checkDate <= maxDate;
};

// Validierung für Buchungsdaten
export const validateBookingData = (data, settings) => {
  const errors = {};
  
  // Name validieren
  if (!data.name || data.name.trim().length < 2) {
    errors.name = 'Name muss mindestens 2 Zeichen lang sein';
  }
  
  // E-Mail validieren
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!data.email || !emailRegex.test(data.email)) {
    errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
  }
  
  // Telefon validieren (Pflichtfeld)
  if (!data.phone || data.phone.trim().length === 0) {
    errors.phone = 'Telefonnummer ist erforderlich';
  } else {
    const phoneRegex = /^[\d\s\-+()]+$/;
    if (!phoneRegex.test(data.phone.trim())) {
      errors.phone = 'Bitte geben Sie eine gültige Telefonnummer ein';
    }
  }
  
  // Personenanzahl validieren
  if (!data.guests || data.guests < 1) {
    errors.guests = 'Mindestens 1 Person erforderlich';
  } else if (data.guests > settings.max_guests_per_booking) {
    errors.guests = `Maximal ${settings.max_guests_per_booking} Personen pro Online-Buchung`;
  }
  
  // Datum validieren
  if (!data.date) {
    errors.date = 'Bitte wählen Sie ein Datum';
  } else {
    if (!isDateInFuture(data.date)) {
      errors.date = 'Das Datum muss in der Zukunft liegen';
    } else if (!isDateWithinAdvanceLimit(data.date, settings.max_advance_days)) {
      errors.date = `Buchungen nur bis zu ${settings.max_advance_days} Tage im Voraus möglich`;
    } else if (isDateClosed(data.date, settings)) {
      errors.date = 'Das Restaurant ist an diesem Tag geschlossen';
    }
  }
  
  // Zeit validieren
  if (!data.time) {
    errors.time = 'Bitte wählen Sie eine Uhrzeit';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}; 